/**
 * Pure predicate for the virtualized infinite-scroll fetch-next-page trigger.
 *
 * Extracted so the decision logic can be exercised by unit tests without a
 * DOM, a virtualizer, or a React-Query client. See
 * `__tests__/client/shouldFetchNextPage.test.ts` for the regression cases
 * that motivated it.
 */
export function shouldFetchNextPage(args: {
    /** Index of the last-rendered virtual row, or `undefined` when the
     *  virtualizer has no rows (e.g. `status === 'pending'` on a fresh query). */
    lastItemIndex: number | undefined;
    /** Total number of virtual rows currently known to the virtualizer. */
    rowCount: number;
    /** `useInfiniteQuery().hasNextPage` — `undefined` until the first page
     *  has resolved. We treat `undefined` as "unknown → don't fetch yet". */
    hasNextPage: boolean | undefined;
    /** `useInfiniteQuery().isFetchingNextPage`. */
    isFetchingNextPage: boolean;
    /** How many rows from the end is "near enough" to prefetch. Default 2. */
    threshold?: number;
}): boolean {
    if (args.lastItemIndex === undefined) return false;
    if (!args.hasNextPage) return false;
    if (args.isFetchingNextPage) return false;
    return args.lastItemIndex >= args.rowCount - (args.threshold ?? 2);
}
